import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Search, RefreshCw, Download, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

interface DonationOrder {
  id: number;
  user_id: number;
  username: string;
  amount: string;
  is_anonymous: number;
  system_note: string;
  txid: string | null;
  sender_address: string | null;
  status: string;
  created_at: string;
}

const AdminDonationOrders: React.FC = () => {
  console.log('AdminDonationOrders component created');
  const [orders, setOrders] = useState<DonationOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    username: '',
    startDate: '',
    endDate: ''
  });
  const pageSize = 20;

  useEffect(() => {
    console.log('AdminDonationOrders mounted');
    loadOrders();
  }, []);
  
  useEffect(() => {
    loadOrders();
  }, [page, filters.username, filters.startDate, filters.endDate]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      console.log('loadOrders called');
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      if (filters.username) params.append('username', filters.username);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      console.log('Request URL:', `/reconciliation/admin/donation-orders?${params}`);
      const response = await api.get<{ success: boolean; data: DonationOrder[]; pagination: { page: number; pageSize: number; total: number } }>(`/reconciliation/admin/donation-orders?${params}`);
      console.log('Response:', response);

      if (response.success) {
        console.log('Data is success, orders:', response.data);
        console.log('Total:', response.pagination?.total);
        setOrders(response.data);
        setTotal(response.pagination?.total || 0);
      } else {
        console.log('Data is not success');
      }
    } catch (error: any) {
      console.error('加载捐助订单失败:', error);
      console.error('Error response:', error.response);
      toast.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadOrders();
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '待支付' },
      success: { bg: 'bg-green-100', text: 'text-green-800', label: '已到账' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const formatAddress = (address: string | null) => {
    if (!address) return '-';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatTime = (time: string) => {
    return new Date(time).toLocaleString('zh-CN');
  };

  const exportOrders = () => {
    const csv = [
      ['ID', '用户名', '金额', '是否匿名', '备注', 'TXID', '状态', '捐助时间'].join(','),
      ...orders.map(order => [
        order.id,
        order.is_anonymous === 1 ? '匿名用户' : order.username,
        order.amount,
        order.is_anonymous === 1 ? '是' : '否',
        order.system_note,
        order.txid || '-',
        order.status,
        formatTime(order.created_at)
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `donation_orders_${Date.now()}.csv`;
    link.click();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">捐助订单流水</h1>
        <button
          onClick={exportOrders}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
        >
          <Download className="w-4 h-4" />
          导出流水
        </button>
      </div>

      {/* 筛选表单 */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
            <input
              type="text"
              value={filters.username}
              onChange={(e) => setFilters({ ...filters, username: e.target.value })}
              placeholder="搜索用户名"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">开始时间</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">结束时间</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
            >
              <Search className="w-4 h-4" />
              搜索
            </button>
            <button
              type="button"
              onClick={() => {
                setFilters({ username: '', startDate: '', endDate: '' });
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              重置
            </button>
          </div>
        </div>
      </form>

      {/* 数据表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">用户名</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">金额</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">是否匿名</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">备注</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">TXID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">状态</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">捐助时间</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin text-pink-500" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-500">暂无数据</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{order.id}</td>
                    <td className="py-3 px-4 text-sm font-medium">
                      {order.is_anonymous === 1 ? (
                        <span className="text-gray-500">匿名用户</span>
                      ) : (
                        order.username
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm font-bold text-green-600">{Number(order.amount).toFixed(2)} USDT</td>
                    <td className="py-3 px-4 text-sm">{order.is_anonymous === 1 ? '是' : '否'}</td>
                    <td className="py-3 px-4 text-sm font-mono text-xs">{order.system_note}</td>
                    <td className="py-3 px-4 text-sm font-mono text-xs">
                      {order.txid ? (
                        <a
                          href={`https://tronscan.org/#/transaction/${order.txid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-600 hover:underline"
                        >
                          {formatAddress(order.txid)}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(order.status)}</td>
                    <td className="py-3 px-4 text-sm">{formatTime(order.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
            <div className="text-sm text-gray-600">
              共 {total} 条记录，第 {page} / {Math.ceil(total / pageSize)} 页
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                上一页
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / pageSize)}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDonationOrders;
